/**
 * Аналитика прошлых лет — «что и сколько давали поля».
 *
 * Wireframe (одобрен founder 07.08.2026):
 *   `docs/superpowers/prototypes/2026-08-07-history-analytics/index.html`
 * Решения по развилкам А-Ж:
 *   `docs/superpowers/specs/2026-08-07-history-analytics-decisions.md`
 *
 * ОДИН компонент, ДВЕ точки монтирования (решение А):
 *   1. самостоятельный раздел «Аналитика» → роут `/analytics/history`;
 *   2. опора внутри планирования → вход из бюджета сезона.
 * Разница между входами ровно в двух вещах: хлебные крошки и кнопка «Взять в
 * план» (её показываем только когда известно, в какой бюджет подставлять).
 * Фильтры, таблицы и расчёты — общие; копии экрана нет.
 *
 * Что здесь принципиально и не подлежит «упрощению»:
 *
 * * урожайность — **бункерный вес**, всегда **ц/га**; подпись стоит и плашкой
 *   в шапке, и под каждым заголовком колонки (решения В, Г);
 * * средняя — **взвешенная по площади**, считает бэкенд; фронт её не
 *   пересчитывает и не «усредняет средние»;
 * * прочерк ≠ ноль. `—` значит «не знаем» (уборку не закрыли), `0,0` значит
 *   «убрали и не получили»; пар / АМП — «не применимо». Три разных состояния,
 *   три разных отрисовки;
 * * штриховка в матрице = поля тогда не существовало (год вне
 *   `first_history_year…last_history_year`), а не «ноль»;
 * * вал в тоннах — вторым планом, `text-muted-foreground` (решение Ж);
 * * графических библиотек нет: полосы и спарклайны — CSS на semantic-токенах.
 */
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { ChevronDown, ChevronRight, Download, Info } from 'lucide-react';
import { AppShell } from '@/components/layout/AppShell';
import { PageHeader } from '@/components/layout/PageHeader';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { CropChip } from '@/components/ui/crop-chip';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Sheet, SheetContent, SheetDescription, SheetTitle } from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { Stack } from '@/components/ui/stack';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { TileGrid } from '@/components/ui/tile-grid';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { FarmScopeNotice } from '@/components/scope/FarmScopeNotice';
import { findScope, topLevelScopeName, type ScopeSelection } from '@/components/scope/ScopeTree';
import { useAvailableScopes } from '@/components/scope/useAvailableScopes';
import { useFarmDeepLink } from '@/components/scope/useFarmDeepLink';
import { useScopeChanged } from '@/lib/scopeEvent';
import {
  barPct,
  fmtArea,
  fmtYield,
  getCropsHistory,
  getFieldHistoryCard,
  getFieldsHistory,
  maxYield,
  type CropHistoryRow,
  type FieldHistoryRow,
  type HistoryPeriod,
  type HistorySummary,
} from '@/lib/analytics/historyApi';
import { cn } from '@/lib/utils';

/**
 * Тот же паттерн, что в `PesticidesCatalogPage`: поиск не должен бить по
 * агрегатной ручке на каждый keystroke.
 */
function useDebounced(value: string, delay = 300): string {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

/** Русское склонение: `pluralRu(3, 'посев', 'посева', 'посевов')` → «посева». */
export function pluralRu(n: number, one: string, few: string, many: string): string {
  const abs = Math.abs(Math.trunc(n));
  const d10 = abs % 10;
  const d100 = abs % 100;
  if (d10 === 1 && d100 !== 11) return one;
  if (d10 >= 2 && d10 <= 4 && (d100 < 12 || d100 > 14)) return few;
  return many;
}

const ALL_CROPS = '__all__';
const PAGE_SIZE = 50;

/** Область сессии ещё не пришла — запросов нет (`scopeReady`), это лишь заглушка типа. */
const WHOLE_COMPANY: ScopeSelection = { scope_type: 'company', scope_id: null };

/**
 * Выбор в пикере → параметры запроса.
 *
 * Кластер НЕ схлопывается в список хозяйств на клиенте: разворачивает его бэк
 * (`cluster_id` → `farms WHERE cluster_id = …`), потому что принадлежность
 * хозяйства кластеру живёт в БД, и второй источник правды на фронте разъехался
 * бы при первом же переносе хозяйства между кластерами.
 *
 * «Вся компания» уезжает ЯВНЫМ флагом `wholeCompany`, а не «пустыми
 * параметрами»: запрос без farm_id/cluster_id бэк не отличает от старого
 * клиента и применяет линзу сессии — так на staging v1.1742 локальная «вся
 * компания» показывала данные фермы под шапкой компании (UAT founder 21.08).
 * Любой явный выбор экрана (компания/кластер/ферма) ЗАМЕЩАЕТ линзу сессии на
 * бэке (`_field_scope`); границы прав от этого не двигаются — они в схеме
 * тенанта и RBAC-гейте, а не в линзе.
 */
export function scopeToFilters(s: ScopeSelection): {
  farmId: string | null;
  clusterId: string | null;
  wholeCompany: boolean;
} {
  return {
    farmId: s.scope_type === 'farm' ? s.scope_id : null,
    clusterId: s.scope_type === 'cluster' ? s.scope_id : null,
    wholeCompany: s.scope_type === 'company',
  };
}

/**
 * Имя верхнего уровня для контролов области (v2-17: «по имени компании»).
 *
 * `/scope/available` называет company-строку литералом «Вся компания»
 * (захардкожено на бэке в `scope.py` — Core-файл, переименование там задело
 * бы и глобальный дровер, отдельный разговор с head-backend). Настоящее имя
 * холдинга в том же ответе несут `holding`-строки — берём его, нового запроса
 * не нужно. Сравнение с литералом — осознанный мост: если бэк однажды начнёт
 * отдавать настоящее имя в company-строке, оно подхватится само.
 */
// Хелпер переехал в общий `@/components/scope/ScopeTree` — им пользуется уже
// третий экран (бюджетные справочники). Ре-экспорт оставлен, чтобы не менять
// импорты соседей и тестов этого экрана.
export { topLevelScopeName };



/** Контекст монтирования: от него зависят только крошки и «Взять в план». */
export type HistoryContext = 'standalone' | 'planning';

// ─── Плашка «бункерный вес» ─────────────────────────────────────────────────

/**
 * Обязательная подпись (решение В). Переключателя зачёт/бункер в v1 нет —
 * значит, единственная защита от «а это какой вес?» это то, что подпись видна
 * всегда, а не по наведению.
 */
function BunkerBadge() {
  return (
    <Badge variant="accent-brand" className="gap-1">
      <Info className="h-3 w-3" />
      Урожайность — бункерный вес
    </Badge>
  );
}

/** Подпись под заголовком колонки урожайности. Дублирует плашку намеренно. */
function UnitNote({ children = 'бункерный вес' }: { children?: string }) {
  return <span className="block text-[10px] font-medium normal-case text-accent-brand">{children}</span>;
}

// ─── CSS-полосы вместо графиков ─────────────────────────────────────────────

function Bar({ value, max, muted }: { value: number | null; max: number; muted?: boolean }) {
  const pct = barPct(value, max);
  return (
    <div className="h-4 w-full min-w-16 overflow-hidden rounded-md bg-muted">
      <div
        className={cn('h-full rounded-md', muted ? 'bg-accent-brand' : 'bg-primary')}
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}

/**
 * Спарклайн по годам. Год без урожайности рисуется «пеньком» из `bg-muted` —
 * визуально это дырка в истории, а не низкий урожай.
 */
function Sparkline({ row }: { row: CropHistoryRow }) {
  const max = maxYield(row.years);
  const avg = row.summary.avg_yield_c_ha;
  return (
    <div className="min-w-24">
      <div className="flex h-8 items-end gap-1">
        {row.years.map((y) => {
          const known = y.yield_c_ha !== null;
          const below = known && avg !== null && (y.yield_c_ha as number) < avg;
          return (
            <div
              key={y.year}
              className={cn(
                'flex-1 rounded-t-sm',
                !known ? 'bg-muted' : below ? 'bg-accent-brand' : 'bg-primary',
              )}
              style={{ height: known ? `${barPct(y.yield_c_ha, max)}%` : '12%' }}
              title={`${y.year}: ${fmtYield(y.yield_c_ha)} ц/га`}
            />
          );
        })}
      </div>
      <div className="flex gap-1">
        {row.years.map((y) => (
          <span key={y.year} className="flex-1 text-center text-[10px] text-muted-foreground">
            {String(y.year).slice(2)}
          </span>
        ))}
      </div>
    </div>
  );
}

/** Мин — среднее — макс одной строкой: «из чего сложилась средняя». */
function MinMax({ summary }: { summary: HistorySummary }) {
  const { min_yield_c_ha: min, max_yield_c_ha: max, avg_yield_c_ha: avg } = summary;
  if (min === null || max === null) return <span className="text-muted-foreground">—</span>;
  const span = max - min;
  const avgPct = span > 0 && avg !== null ? ((avg - min) / span) * 100 : 50;
  return (
    <div className="flex items-center gap-2">
      <span className="tabular-nums text-xs text-muted-foreground">{fmtYield(min)}</span>
      <span className="relative h-2 min-w-16 flex-1 rounded-full bg-muted">
        <span className="absolute inset-y-0 left-0 right-0 rounded-full bg-primary/20" />
        <span
          className="absolute -top-1 h-4 w-0.5 bg-foreground"
          style={{ left: `${Math.min(100, Math.max(0, avgPct))}%` }}
        />
      </span>
      <span className="tabular-nums text-xs text-muted-foreground">{fmtYield(max)}</span>
    </div>
  );
}

/**
 * Колонка «Что важно знать» — предупреждения из wireframe (состояние 6).
 *
 * Это не украшение: свод — тот самый экран, с которого число уезжает в план.
 * Средняя по одному году средней не является, а культура с посевами и без
 * закрытой уборки не «плохая», а неизмеренная. Молчаливый прочерк на её месте
 * агроном прочитает как «ноль» — и это худшее из возможных прочтений.
 */
export function DataQualityNote({ row, summary }: { row: CropHistoryRow; summary: HistorySummary }) {
  if (!row.yield_applicable) {
    // Пар / АМП — в обеих ипостасях (состояние поля и культура-пар каталога).
    // Если в посевах пара сидит урожайность — это не урожай, а ошибочная
    // классификация: цифру не показываем, но и не глотаем молча.
    return (
      <span className="text-xs text-muted-foreground">
        {row.stray_yield_sowings > 0 && (
          <Badge variant="accent-brand" className="mr-2">
            требует разбора
          </Badge>
        )}
        Пар / АМП — урожайности не бывает по определению.
        {row.stray_yield_sowings > 0 && (
          <>
            {' '}
            {row.stray_yield_sowings} {pluralRu(row.stray_yield_sowings, 'посев', 'посева', 'посевов')}{' '}
            с урожаем {row.stray_yield_sowings === 1 ? 'выглядит' : 'выглядят'} не паром —{' '}
            {row.stray_yield_sowings === 1 ? 'требует' : 'требуют'} разбора.
          </>
        )}
      </span>
    );
  }
  if (summary.years_with_yield === 0) {
    return (
      <span className="text-xs text-muted-foreground">
        <Badge variant="accent-brand" className="mr-2">
          урожайность не вносилась
        </Badge>
        Посевы есть, уборка не закрыта — считать нечего. Площади видны.
      </span>
    );
  }
  if (summary.years_with_yield === 1) {
    return (
      <span className="text-xs text-muted-foreground">
        <Badge variant="accent-brand" className="mr-2">
          мало данных
        </Badge>
        Один год — это не средняя. Опираться на неё в плане рискованно.
      </span>
    );
  }
  const missing = summary.sowings_count - summary.sowings_with_yield;
  if (missing > 0) {
    return (
      <span className="text-xs text-muted-foreground">
        Урожайность известна по {summary.sowings_with_yield} посевам из{' '}
        {summary.sowings_count}.
      </span>
    );
  }
  return <span className="text-xs text-muted-foreground">—</span>;
}

// ─── Свод по культурам ──────────────────────────────────────────────────────

function CropsTable({
  items,
  period,
  excludeCurrent,
  context,
  onOpenFields,
}: {
  items: CropHistoryRow[];
  period: HistoryPeriod;
  excludeCurrent: boolean;
  context: HistoryContext;
  onOpenFields: (cropId: string) => void;
}) {
  const [open, setOpen] = useState<string | null>(null);
  const pick = (r: CropHistoryRow) => (excludeCurrent ? r.summary_excl_current : r.summary);

  const totals = useMemo(() => {
    const rows = items.map(pick);
    return {
      area: rows.reduce((s, r) => s + (r.avg_area_ha ?? 0), 0),
      gross: rows.reduce((s, r) => s + (r.avg_gross_t ?? 0), 0),
      years: Math.max(0, ...rows.map((r) => r.years_count)),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, excludeCurrent]);

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="w-[22%]">Культура</TableHead>
          <TableHead className="text-right">Лет</TableHead>
          <TableHead className="text-right">Площадь ср., га</TableHead>
          <TableHead className="text-right">
            Урожайность ср., ц/га
            <UnitNote />
          </TableHead>
          <TableHead className="w-[16%]">Мин — макс по годам</TableHead>
          <TableHead className="w-[14%]">По годам</TableHead>
          <TableHead className="text-right font-normal text-muted-foreground">Вал ср., т</TableHead>
          <TableHead className="w-[16%]">Что важно знать</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {items.map((row) => {
          const s = pick(row);
          const isOpen = open === row.key;
          return (
            // Fragment с КЛЮЧОМ, не `<>`: элемент массива — сам фрагмент, и
            // ключи на его детях React не видит. `items` фильтруется по
            // культуре и меняется при смене хозяйства, так что ключевание по
            // индексу переиспользовало бы раскрытую строку под чужие данные.
            <Fragment key={row.key}>
              <TableRow
                className="cursor-pointer"
                onClick={() => setOpen(isOpen ? null : row.key)}
              >
                <TableCell>
                  <div className="flex items-center gap-2">
                    {isOpen ? (
                      <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
                    ) : (
                      <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                    )}
                    <CropChip name={row.name} className="font-medium" />
                  </div>
                  <div className="pl-6 text-xs text-muted-foreground">
                    {s.sowings_count} посевов
                    {!row.yield_applicable && (
                      <Badge variant="outline" className="ml-2">
                        пар / АМП
                      </Badge>
                    )}
                  </div>
                </TableCell>
                <TableCell className="text-right tabular-nums">{s.years_count}</TableCell>
                <TableCell className="text-right tabular-nums">{fmtArea(s.avg_area_ha)}</TableCell>
                <TableCell className="text-right tabular-nums font-medium">
                  {row.yield_applicable ? (
                    fmtYield(s.avg_yield_c_ha)
                  ) : (
                    <span className="text-xs font-normal text-muted-foreground">не применимо</span>
                  )}
                </TableCell>
                <TableCell>
                  {row.yield_applicable ? (
                    <MinMax summary={s} />
                  ) : (
                    <span className="text-xs text-muted-foreground">
                      поле без продуктивной культуры
                    </span>
                  )}
                </TableCell>
                <TableCell>
                  {row.yield_applicable ? (
                    <Sparkline row={row} />
                  ) : (
                    <span className="text-xs text-muted-foreground">учитывается площадью</span>
                  )}
                </TableCell>
                <TableCell className="text-right tabular-nums text-muted-foreground">
                  {row.yield_applicable ? fmtArea(s.avg_gross_t) : '—'}
                </TableCell>
                <TableCell>
                  <DataQualityNote row={row} summary={s} />
                </TableCell>
              </TableRow>
              {isOpen && (
                <TableRow>
                  <TableCell colSpan={8} className="bg-muted/40">
                    <YearsBreakdown
                      row={row}
                      period={period}
                      excludeCurrent={excludeCurrent}
                      context={context}
                      onOpenFields={onOpenFields}
                    />
                  </TableCell>
                </TableRow>
              )}
            </Fragment>
          );
        })}
        <TableRow className="bg-muted/60 font-medium">
          <TableCell>Итого по выбранным фильтрам</TableCell>
          <TableCell className="text-right tabular-nums">{totals.years}</TableCell>
          <TableCell className="text-right tabular-nums">{fmtArea(totals.area)}</TableCell>
          <TableCell className="text-right text-xs font-normal text-muted-foreground">
            средняя по разным культурам не считается
          </TableCell>
          <TableCell />
          <TableCell />
          <TableCell className="text-right tabular-nums text-muted-foreground">
            {fmtArea(totals.gross)}
          </TableCell>
          <TableCell />
        </TableRow>
      </TableBody>
    </Table>
  );
}

/**
 * Раскрытие культуры по годам — «из чего сложилась средняя».
 *
 * Данные уже приехали вместе со сводом: клик по строке не ходит в сеть.
 */
function YearsBreakdown({
  row,
  period,
  excludeCurrent,
  context,
  onOpenFields,
}: {
  row: CropHistoryRow;
  period: HistoryPeriod;
  excludeCurrent: boolean;
  context: HistoryContext;
  onOpenFields: (cropId: string) => void;
}) {
  const max = maxYield(row.years);
  const shown = excludeCurrent
    ? row.years.filter((y) => y.year !== period.current_year)
    : row.years;
  const s = excludeCurrent ? row.summary_excl_current : row.summary;
  const other = excludeCurrent ? row.summary : row.summary_excl_current;

  return (
    <Stack gap={3}>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Год</TableHead>
            <TableHead className="text-right">Площадь, га</TableHead>
            <TableHead className="text-right">
              Урожайность, ц/га
              <UnitNote />
            </TableHead>
            <TableHead className="text-right font-normal text-muted-foreground">Вал, т</TableHead>
            {/* Длина полосы — доля от лучшего года, цвет — «ниже средней».
                Подпись обязана описывать обе половины, иначе она врёт. */}
            <TableHead className="w-[24%]">Относительно лучшего года · ниже средней — янтарным</TableHead>
            <TableHead>Заметка</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {shown.map((y) => (
            <TableRow key={y.year}>
              <TableCell className="tabular-nums">{y.year}</TableCell>
              <TableCell className="text-right tabular-nums">{fmtArea(y.area_ha)}</TableCell>
              <TableCell className="text-right tabular-nums font-medium">
                {row.yield_applicable ? fmtYield(y.yield_c_ha) : '—'}
              </TableCell>
              <TableCell className="text-right tabular-nums text-muted-foreground">
                {fmtArea(y.gross_t)}
              </TableCell>
              <TableCell>
                <Bar
                  value={y.yield_c_ha}
                  max={max}
                  muted={
                    y.yield_c_ha !== null &&
                    s.avg_yield_c_ha !== null &&
                    y.yield_c_ha < s.avg_yield_c_ha
                  }
                />
              </TableCell>
              <TableCell className="text-xs text-muted-foreground">
                {!y.is_final && <Badge variant="accent-brand">не финал</Badge>}
                {/* «Не закрыта» — заметка КУЛЬТУРЫ: у пара уборки не бывает,
                    та же фраза на 5 000 посевах пара была бы бессмыслицей. */}
                {row.yield_applicable && y.is_final && y.sowings_with_yield < y.sowings_count && (
                  <span>
                    урожайность не закрыта по {y.sowings_count - y.sowings_with_yield} посевам
                  </span>
                )}
                {/* Пар с урожайностью: цифру не показываем (она не урожай),
                    но след ошибочной классификации проговариваем. */}
                {!row.yield_applicable && y.sowings_with_yield > 0 && (
                  <span>
                    {y.sowings_with_yield}{' '}
                    {pluralRu(y.sowings_with_yield, 'посев', 'посева', 'посевов')} с урожаем{' '}
                    {y.sowings_with_yield === 1 ? 'выглядит' : 'выглядят'} не паром —{' '}
                    {y.sowings_with_yield === 1 ? 'требует' : 'требуют'} разбора
                  </span>
                )}
              </TableCell>
            </TableRow>
          ))}
          <TableRow className="bg-background font-medium">
            <TableCell>Среднее</TableCell>
            <TableCell className="text-right tabular-nums">{fmtArea(s.avg_area_ha)}</TableCell>
            <TableCell className="text-right tabular-nums">
              {row.yield_applicable ? fmtYield(s.avg_yield_c_ha) : '—'}
            </TableCell>
            <TableCell className="text-right tabular-nums text-muted-foreground">
              {fmtArea(s.avg_gross_t)}
            </TableCell>
            <TableCell className="text-xs font-normal text-muted-foreground">
              взвешено по площади
            </TableCell>
            <TableCell className="text-xs font-normal text-muted-foreground">
              {other.avg_yield_c_ha !== null && other.avg_yield_c_ha !== s.avg_yield_c_ha
                ? `${excludeCurrent ? 'с' : 'без'} ${period.current_year} — ${fmtYield(other.avg_yield_c_ha)}`
                : null}
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>

      <div className="flex flex-wrap items-center justify-end gap-2">
        {row.crop_id && (
          <Button variant="outline" size="sm" onClick={() => onOpenFields(row.crop_id as string)}>
            Смотреть по полям →
          </Button>
        )}
        {context === 'planning' && row.yield_applicable && (
          <Tooltip>
            <TooltipTrigger asChild>
              {/* span нужен, чтобы тултип ловил наведение на disabled-кнопке */}
              <span tabIndex={0}>
                <Button size="sm" disabled>
                  Взять {fmtYield(s.avg_yield_c_ha)} в план
                </Button>
              </span>
            </TooltipTrigger>
            <TooltipContent>
              Появится вместе со структурой посевных площадей — подставлять число
              пока некуда.
            </TooltipContent>
          </Tooltip>
        )}
      </div>
    </Stack>
  );
}

// ─── Матрица «поле × год» ───────────────────────────────────────────────────

function FieldsMatrix({
  data,
  onOpenField,
}: {
  data: { period: HistoryPeriod; items: FieldHistoryRow[]; total: number };
  onOpenField: (fieldId: string) => void;
}) {
  const years = data.period.years;
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="w-[18%]">Поле</TableHead>
          <TableHead className="text-right">Площадь, га</TableHead>
          {years.map((y) => (
            <TableHead key={y} className="text-center">
              {y}
            </TableHead>
          ))}
          <TableHead className="text-right">
            Средняя за годы с урожаем
            <UnitNote>ц/га, бункерный вес</UnitNote>
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {data.items.map((f) => {
          const byYear = new Map(f.cells.map((c) => [c.year, c]));
          return (
            <TableRow
              key={f.field_id}
              className="cursor-pointer"
              onClick={() => onOpenField(f.field_id)}
            >
              <TableCell>
                <div className="font-medium">{f.name}</div>
                <div className="text-xs text-muted-foreground">
                  {[f.farm_name, f.brigade_name].filter(Boolean).join(' · ')}
                </div>
              </TableCell>
              <TableCell className="text-right tabular-nums">{fmtArea(f.area_ha)}</TableCell>
              {years.map((y) => {
                const cell = byYear.get(y);
                // Три разных пустоты, три разных вида. Штриховка = поля тогда
                // не было; пустая ячейка = год без занятия; «нет данных» =
                // посев был, урожайность не внесли.
                const outside =
                  f.first_history_year !== null &&
                  (y < f.first_history_year ||
                    (f.last_history_year !== null && y > f.last_history_year));
                if (!cell) {
                  return (
                    <TableCell
                      key={y}
                      className={cn(
                        'text-center text-xs text-muted-foreground',
                        outside && 'bg-muted/50',
                      )}
                      title={outside ? 'поля тогда не было в обработке' : 'занятия не было'}
                    >
                      —
                    </TableCell>
                  );
                }
                return (
                  <TableCell key={y} className="text-center">
                    <CropChip
                      name={cell.crop_name}
                      className="justify-center text-xs font-medium"
                    />
                    <div
                      className={cn(
                        'tabular-nums text-sm font-semibold',
                        cell.yield_c_ha === null && 'text-xs font-normal text-muted-foreground',
                      )}
                    >
                      {cell.kind === 'field_state'
                        ? '—'
                        : cell.yield_c_ha === null
                          ? 'нет данных'
                          : fmtYield(cell.yield_c_ha)}
                    </div>
                    {/* Пересев не маскируем: показываем урожайность ТОЙ культуры,
                        что заняла больше, и честно говорим, что в году была не
                        только она. Смешивать их в одно число нельзя. */}
                    {cell.other_crops.length > 0 && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span tabIndex={0}>
                            <Badge variant="outline" className="mt-1 cursor-help">
                              ещё {cell.other_crops.length}
                            </Badge>
                          </span>
                        </TooltipTrigger>
                        <TooltipContent>
                          В этом году поле занимали также: {cell.other_crops.join(', ')}.
                          Показана урожайность культуры с наибольшей площадью — общей
                          урожайности у разных культур не бывает.
                        </TooltipContent>
                      </Tooltip>
                    )}
                  </TableCell>
                );
              })}
              <TableCell className="text-right">
                <span className="tabular-nums font-medium">{fmtYield(f.avg_yield_c_ha)}</span>
                {f.avg_crop_name && (
                  <div className="text-xs text-muted-foreground">
                    по культуре «{f.avg_crop_name}»
                    {f.avg_years_count === 1 && ' · 1 год'}
                  </div>
                )}
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}

// ─── Карточка поля ──────────────────────────────────────────────────────────

const SOURCE_LABEL: Record<string, string> = {
  agrohistory: 'АгроХистори',
  manual: 'введено в системе',
  mixed: 'частично из АгроХистори',
};

function FieldCardSheet({
  fieldId,
  period,
  scope,
  onClose,
}: {
  fieldId: string | null;
  period: HistoryPeriod;
  /**
   * Локальный выбор области экрана — тот же, что у матрицы. Карточка обязана
   * открываться на любое поле, которое матрица только что показала: без этих
   * параметров бэк применил бы линзу сессии и ответил 404 на поле чужой
   * (для линзы) фермы (тот же класс бага, что и «вся компания», UAT 21.08).
   */
  scope: { farmId: string | null; clusterId: string | null; wholeCompany: boolean };
  onClose: () => void;
}) {
  const q = useQuery({
    queryKey: [
      'analytics-history',
      'field',
      fieldId,
      scope.farmId,
      scope.clusterId,
      scope.wholeCompany,
      period.year_from,
      period.year_to,
    ],
    queryFn: () =>
      getFieldHistoryCard(fieldId as string, {
        farmId: scope.farmId,
        clusterId: scope.clusterId,
        wholeCompany: scope.wholeCompany,
        yearFrom: period.year_from,
        yearTo: period.year_to,
      }),
    enabled: !!fieldId,
  });
  const card = q.data;
  const max = card ? maxYield(card.rows) : 0;

  return (
    <Sheet open={!!fieldId} onOpenChange={(v) => !v && onClose()}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-3xl">
        <SheetTitle>
          {card ? `${card.name} — ${fmtArea(card.area_ha)} га` : 'История поля'}
        </SheetTitle>
        <SheetDescription>
          {card
            ? `${[card.farm_name, card.brigade_name].filter(Boolean).join(', ')} · история ${period.year_from}–${period.year_to}`
            : 'Загружаем историю урожайности'}
        </SheetDescription>

        {q.isLoading && <Skeleton className="mt-4 h-64 w-full" />}
        {card && (
          <Stack gap={4} className="mt-4">
            <BunkerBadge />
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Год</TableHead>
                  <TableHead>Культура</TableHead>
                  <TableHead className="text-right">Площадь, га</TableHead>
                  <TableHead className="text-right">
                    Урожайность, ц/га
                    <UnitNote />
                  </TableHead>
                  <TableHead className="text-right font-normal text-muted-foreground">
                    Вал, т
                  </TableHead>
                  <TableHead className="w-[18%]">Относительно лучшего года</TableHead>
                  <TableHead>Источник</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {/* Строка = ЗАНЯТИЕ, а не год: год с пересевом даёт две строки
                    со своими урожайностями. Ключ по (year, crop) — в одном году
                    может быть несколько культур. */}
                {card.rows.map((r, i) => (
                  <TableRow key={`${r.year}:${r.crop_id ?? r.crop_name}`}>
                    <TableCell className="tabular-nums">
                      {i > 0 && card.rows[i - 1].year === r.year ? (
                        <span className="text-muted-foreground">↳</span>
                      ) : (
                        r.year
                      )}
                    </TableCell>
                    <TableCell>
                      <CropChip name={r.crop_name} />
                      {r.kind === 'field_state' && (
                        <Badge variant="outline" className="ml-2">
                          пар / АМП
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{fmtArea(r.area_ha)}</TableCell>
                    <TableCell className="text-right tabular-nums font-medium">
                      {fmtYield(r.yield_c_ha)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-muted-foreground">
                      {fmtArea(r.gross_t)}
                    </TableCell>
                    <TableCell>
                      {r.kind === 'field_state' ? (
                        <span className="text-xs text-muted-foreground">поле отдыхало</span>
                      ) : (
                        <Bar value={r.yield_c_ha} max={max} />
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant={r.source === 'manual' ? 'outline' : 'secondary'}>
                        {SOURCE_LABEL[r.source] ?? r.source}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            <TileGrid cols={4}>
              <Tile
                value={fmtYield(card.lead_crop_summary?.avg_yield_c_ha ?? null)}
                label={
                  card.lead_crop_name
                    ? `средняя по культуре «${card.lead_crop_name}», ц/га (${card.lead_crop_summary?.years_with_yield ?? 0} г.)`
                    : 'средней нет — не было продуктивных культур'
                }
              />
              <Tile value={String(card.crops_count)} label="культуры в севообороте" />
              <Tile value={String(card.fallow_years)} label="лет под паром / АМП" />
              <Tile value={String(card.years_count)} label="лет истории на этом поле" />
            </TileGrid>

            <Button asChild variant="outline">
              <Link to="/fields/$fieldId" params={{ fieldId: card.field_id }}>
                Открыть карточку поля
              </Link>
            </Button>
          </Stack>
        )}
      </SheetContent>
    </Sheet>
  );
}

function Tile({ value, label }: { value: string; label: string }) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="text-xl font-semibold tabular-nums">{value}</div>
        <div className="text-xs text-muted-foreground">{label}</div>
      </CardContent>
    </Card>
  );
}

// ─── Пустые состояния ───────────────────────────────────────────────────────

function NoHistory({ farmName }: { farmName: string | null }) {
  return (
    <Card>
      <CardContent className="p-4 text-center">
        <div className="text-base font-semibold">
          {farmName ? `По хозяйству «${farmName}» истории пока нет` : 'Истории пока нет'}
        </div>
        <p className="mt-2 text-sm text-muted-foreground">
          Мы ничего не знаем о прошлых урожаях — значит, план придётся ставить по
          нормативу, а не по факту.
        </p>
      </CardContent>
    </Card>
  );
}

// ─── Экран целиком ──────────────────────────────────────────────────────────

export interface HistoryAnalyticsProps {
  /** `planning` добавляет крошки бюджета сезона и кнопку «Взять в план». */
  context?: HistoryContext;
  /**
   * `?farm_id=` — deep-link (вход «смотреть» из строки СПП, из карточки
   * хозяйства). Расходится с областью сессии — экран переключает область тем
   * же путём, что и переключатель (`useScopeSetter`), и зовёт
   * `onFarmLinkApplied`, чтобы адрес забыл параметр (AK-897).
   */
  initialFarmId?: string | null;
  /** Убрать `?farm_id=` из адреса, когда ссылка отработала. */
  onFarmLinkApplied?: () => void;
  /** Предвыбранная культура — вход «смотреть» из строки СПП. */
  initialCropId?: string | null;
  /** Юрлицо, выбранное в хабе планирования (AK-541). */
  companyId?: string | null;
}

export function HistoryAnalytics({
  context = 'standalone',
  initialFarmId = null,
  onFarmLinkApplied,
  initialCropId = null,
  companyId = null,
}: HistoryAnalyticsProps) {
  const [tab, setTab] = useState<'crops' | 'fields'>(initialCropId ? 'fields' : 'crops');
  const [cropId, setCropId] = useState<string | null>(initialCropId);
  const [excludeCurrent, setExcludeCurrent] = useState(false);
  const [search, setSearch] = useState('');
  const [openField, setOpenField] = useState<string | null>(null);

  const qc = useQueryClient();
  const debouncedSearch = useDebounced(search);

  // Область экрана = область сессии (AK-866 В-3: область — из переключателя).
  // Своего фильтра области на экране больше нет: прежний фильтр «область»
  // выбирал ОДНУ область (компания / кластер / хозяйство) — это контекст, а не
  // набор хозяйств для сравнения, и он дублировал переключатель слева (AK-897).
  // Аналитика, в отличие от плана, работает и на кластере, и на всём холдинге:
  // в переключателе «Холдинг» — история по всей компании.
  //
  // `/scope/available` — общий кэш с переключателем (`useAvailableScopes`):
  // переключение подменяет `current` в нём же, экран перестраивается сам.
  const scope = useAvailableScopes();
  const linkApplied = useRef(onFarmLinkApplied);
  linkApplied.current = onFarmLinkApplied;
  const { link, isOwnSwitch } = useFarmDeepLink(initialFarmId, () => linkApplied.current?.());

  // Решение Е: по умолчанию — то, из чего зашли (хозяйство ИЛИ кластер).
  // Ссылка, которая ещё переключает область, уже показывает своё хозяйство.
  const selection: ScopeSelection =
    link.state === 'switching'
      ? { scope_type: 'farm', scope_id: link.farmId }
      : scope.data
        ? { scope_type: scope.data.current.scope_type, scope_id: scope.data.current.scope_id }
        : WHOLE_COMPANY;
  const {
    farmId: effectiveFarmId,
    clusterId: effectiveClusterId,
    wholeCompany: effectiveWholeCompany,
  } = scopeToFilters(selection);

  // Смена области в переключателе двигает и этот экран: ключи запросов идут от
  // области, но кэш прошлой области сбрасываем, чтобы не мигнуть чужими цифрами.
  useScopeChanged((detail) => {
    if (isOwnSwitch(detail)) return;
    qc.invalidateQueries({ queryKey: ['analytics-history'] });
  });

  // `enabled: !scope.isLoading` — иначе первый рендер (scope ещё не приехал,
  // `effectiveFarmId === null`) гоняет САМЫЙ дорогой вариант агрегата, по
  // всему холдингу, и через мгновение всё пересчитывается заново под ферму.
  // Один лишний полный проход по истории на каждый вход в раздел.
  // Ссылка в хозяйство без доступа / упавшее переключение — истории не просим:
  // показали бы цифры области, о которой ссылка не просила.
  const linkBlocked = link.state === 'no-access' || link.state === 'error';
  const scopeReady = !!scope.data && link.state !== 'waiting' && !linkBlocked;

  const crops = useQuery({
    // `effectiveWholeCompany` стоит в ключе и уезжает флагом в запрос: «вся
    // компания» — явный выбор, замещающий линзу сессии на бэке, а не «пустые
    // параметры» (staging v1.1742: без флага данные оставались фермерскими).
    queryKey: [
      'analytics-history',
      'crops',
      companyId,
      effectiveClusterId,
      effectiveFarmId,
      effectiveWholeCompany,
    ],
    queryFn: () =>
      getCropsHistory({
        farmId: effectiveFarmId,
        clusterId: effectiveClusterId,
        wholeCompany: effectiveWholeCompany,
        companyId,
      }),
    enabled: scopeReady,
    staleTime: 60_000,
  });

  const fields = useQuery({
    queryKey: [
      'analytics-history',
      'fields',
      companyId,
      effectiveClusterId,
      effectiveFarmId,
      effectiveWholeCompany,
      cropId,
      debouncedSearch,
    ],
    queryFn: () =>
      getFieldsHistory({
        farmId: effectiveFarmId,
        clusterId: effectiveClusterId,
        wholeCompany: effectiveWholeCompany,
        companyId,
        cropId,
        search: debouncedSearch.trim() || undefined,
        limit: PAGE_SIZE,
      }),
    enabled: scopeReady && tab === 'fields',
    staleTime: 60_000,
    // Без этого таблица схлопывается в скелетон на каждую букву поиска —
    // ровно «усталостный» баг из CLAUDE.md, а не косметика.
    placeholderData: (prev) => prev,
  });

  const period = crops.data?.period ?? fields.data?.period ?? null;
  const selected = scope.data ? findScope(scope.data.available, selection) : undefined;
  const farmName = selection.scope_type === 'company' ? null : (selected?.name ?? null);
  const cropOptions = useMemo(
    () => (crops.data?.items ?? []).filter((i) => i.crop_id).map((i) => ({ id: i.crop_id as string, name: i.name })),
    [crops.data],
  );

  const coverage = crops.data?.coverage;
  const hasHistory = (crops.data?.items.length ?? 0) > 0;

  return (
    <Stack gap={4}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-muted-foreground">
          Урожайность везде в ц/га. Вал показан вторым планом — он для директора,
          ц/га для агронома.
        </p>
        <BunkerBadge />
      </div>

      {/* Фильтры */}
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-semibold uppercase text-muted-foreground">культура</span>
        <Select
          value={cropId ?? ALL_CROPS}
          onValueChange={(v) => setCropId(v === ALL_CROPS ? null : v)}
        >
          <SelectTrigger className="w-56">
            <SelectValue placeholder="Все культуры" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_CROPS}>Все культуры</SelectItem>
            {cropOptions.map((c) => (
              <SelectItem key={c.id} value={c.id}>
                {c.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {period && (
          <span className="text-xs text-muted-foreground">
            годы: {period.year_from} – {period.year_to}
          </span>
        )}

        {tab === 'fields' && (
          <Input
            className="ml-auto w-64"
            placeholder="Поиск по номеру или имени поля…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        )}
      </div>

      <Tabs value={tab} onValueChange={(v) => setTab(v as 'crops' | 'fields')}>
        <TabsList>
          <TabsTrigger value="crops">По культурам</TabsTrigger>
          <TabsTrigger value="fields">По полям</TabsTrigger>
        </TabsList>
      </Tabs>

      {link.state === 'no-access' && (
        <FarmScopeNotice scope={{ status: 'no-access', message: null, retry: () => undefined }} />
      )}
      {link.state === 'error' && (
        <FarmScopeNotice scope={{ status: 'error', message: link.message, retry: link.retry }} />
      )}
      {scope.isError && !scope.data && (
        <FarmScopeNotice
          scope={{
            status: 'error',
            message: 'Не удалось загрузить список хозяйств.',
            retry: () => void scope.refetch(),
          }}
        />
      )}

      {!linkBlocked && (crops.isLoading || (!scopeReady && !scope.isError)) && (
        <Skeleton className="h-64 w-full" />
      )}
      {crops.isError && (
        <Card>
          <CardContent className="p-4 text-sm text-destructive">
            Не удалось загрузить историю. Обновите страницу.
          </CardContent>
        </Card>
      )}

      {crops.data && !hasHistory && <NoHistory farmName={farmName} />}

      {crops.data && hasHistory && coverage && period && (
        <>
          <TileGrid cols={4}>
            <Tile
              value={String(coverage.years_count)}
              label={`лет истории (${period.year_from}–${period.year_to})`}
            />
            <Tile value={String(coverage.crops_count)} label="культур встречалось" />
            <Tile value={fmtArea(coverage.fields_count)} label="полей в истории" />
            <Tile
              value={
                coverage.with_yield_pct === null ? '—' : `${Math.round(coverage.with_yield_pct)}%`
              }
              label="посевов с известной урожайностью"
            />
          </TileGrid>

          {/* Тумблер незакрытого года (решение Б). Влияет и на средние, и на
              число, которое уходит в план — поэтому стоит рядом с данными,
              а не в фильтрах. */}
          <div className="flex flex-wrap items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm">
            <span className="text-muted-foreground">
              {period.current_year} год ещё не закрыт:
            </span>
            <Button
              size="sm"
              variant={excludeCurrent ? 'ghost' : 'secondary'}
              onClick={() => setExcludeCurrent(false)}
            >
              учитывать {period.current_year}
            </Button>
            <Button
              size="sm"
              variant={excludeCurrent ? 'secondary' : 'ghost'}
              onClick={() => setExcludeCurrent(true)}
            >
              не учитывать
            </Button>
          </div>

          {tab === 'crops' ? (
            <Card>
              <CardContent className="p-4">
                <CropsTable
                  items={
                    cropId ? crops.data.items.filter((i) => i.crop_id === cropId) : crops.data.items
                  }
                  period={period}
                  excludeCurrent={excludeCurrent}
                  context={context}
                  onOpenFields={(id) => {
                    setCropId(id);
                    setTab('fields');
                  }}
                />
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="p-4">
                {fields.isLoading && <Skeleton className="h-64 w-full" />}
                {fields.data && fields.data.items.length === 0 && (
                  <p className="text-sm text-muted-foreground">
                    Полей с историей по этому фильтру нет.
                  </p>
                )}
                {fields.data && fields.data.items.length > 0 && (
                  <Stack gap={3}>
                    <FieldsMatrix data={fields.data} onOpenField={setOpenField} />
                    <p className="text-xs text-muted-foreground">
                      Показано {fields.data.items.length} из {fields.data.total} полей. Пустая
                      ячейка — это не ноль: серая заливка значит, что поля тогда не было в
                      обработке, «нет данных» — что посев был, а уборку не закрыли. Ни то ни
                      другое в средние не попадает.
                    </p>
                  </Stack>
                )}
              </CardContent>
            </Card>
          )}
        </>
      )}

      {period && (
        <FieldCardSheet
          fieldId={openField}
          period={period}
          scope={{
            farmId: effectiveFarmId,
            clusterId: effectiveClusterId,
            wholeCompany: effectiveWholeCompany,
          }}
          onClose={() => setOpenField(null)}
        />
      )}
    </Stack>
  );
}

/** Вход 1 — самостоятельный раздел «Аналитика» (роут `/analytics/history`). */
export function HistoryAnalyticsPage() {
  // `strict: false` — чтобы страница не импортировала роут из `router.tsx`
  // (роут импортирует страницу; строгий вариант дал бы цикл).
  const search = useSearch({ strict: false }) as {
    farm_id?: string;
    crop_id?: string;
    company_id?: string;
    from?: 'planning';
  };
  const navigate = useNavigate();
  const context: HistoryContext = search.from === 'planning' ? 'planning' : 'standalone';
  return (
    // AK-866 В-3: область — из переключателя. Своего фильтра области на
    // экране нет; `?farm_id=` переключает область только через
    // `useScopeSetter` (AK-897).
    <AppShell>
      <div className="mx-auto max-w-screen-2xl pb-safe-fab">
        <PageHeader
          title="Что и сколько давали поля"
          showBack={context === 'planning'}
          backTo={context === 'planning' ? '/planning' : undefined}
          breadcrumbs={
            context === 'planning'
              ? [
                  { label: 'Планирование', to: '/planning' },
                  { label: 'Аналитика прошлых лет' },
                ]
              : [{ label: 'Аналитика' }, { label: 'Аналитика прошлых лет' }]
          }
          description="Заходят просто посмотреть: перед совещанием, перед разговором с директором, при разборе провального года."
          actions={
            <Tooltip>
              <TooltipTrigger asChild>
                <span tabIndex={0}>
                  <Button variant="outline" disabled>
                    <Download className="mr-2 h-4 w-4" />
                    Выгрузить в Excel
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent>Выгрузка появится следующей итерацией</TooltipContent>
            </Tooltip>
          }
        />
        <div className="mt-4">
          <HistoryAnalytics
            context={context}
            initialFarmId={search.farm_id ?? null}
            onFarmLinkApplied={() =>
              void navigate({
                to: '/analytics/history',
                search: { ...search, farm_id: undefined },
                replace: true,
              })
            }
            initialCropId={search.crop_id ?? null}
            companyId={search.company_id ?? null}
          />
        </div>
      </div>
    </AppShell>
  );
}
